import { flow, get, sortBy } from 'lodash'
import { createSelector } from 'reselect'

import { NO_AGENCY_CASE_MANAGER_DROPDOWN_VALUE } from 'src/utils/constants'
import addAllValue from 'src/utils/functions/addAllValue'

import {
  backfillCarePlan$,
  backfillMedication$,
  companyActivityTypesSelector,
  companyCheckInRefSelector,
  companyEducationRefSelector,
  companyMeasurementRefSelector,
  companyTelemedicineRefs$
} from '../company'

const router = (state = {}) => get(state, ['router'], {})

export const router$ = createSelector(router, (router = {}) => router)

const common = (state = {}) => get(state, ['common'], {})

export const commonBillingCodes$ = createSelector(common, (common = {}) =>
  get(common, ['billingCodes'], [])
)

export const commonFacilities$ = createSelector(common, (common = {}) =>
  get(common, ['facilities'], [])
)

export const commonFacilitiesWithAll$ = createSelector(
  commonFacilities$,
  (facilities = []) => addAllValue(facilities, true, 'All Facilities')
)

export const commonCaseManagers$ = createSelector(common, (common = {}) =>
  get(common, ['caseManagers'], [])
)

export const commonCaseManagersWithAll$ = createSelector(
  commonCaseManagers$,
  (commonCaseManagers = []) =>
    addAllValue(commonCaseManagers, true, 'All Managers')
)

export const commonClinicians$ = createSelector(common, (common = {}) =>
  get(common, ['clinicians'], [])
)

export const commonCliniciansWithAll$ = createSelector(
  commonClinicians$,
  (clinicians = []) => addAllValue(clinicians, true, 'All Clinicians')
)

export const commonCaregivers$ = createSelector(common, (common = {}) =>
  get(common, ['caregivers'], [])
)

export const commonCaregiversDropdownWithAll$ = createSelector(
  commonCaregivers$,
  (caregivers = []) => {
    const updCaregivers = caregivers.map(
      ({ firstName, middleName, lastName, uuid }: any) => ({
        key: uuid,
        value: uuid,
        title: [firstName, middleName, lastName]
          .filter((item) => item)
          .join(' ')
      })
    )

    updCaregivers.unshift({
      key: 0,
      value: null,
      title: 'All caregivers'
    })

    return updCaregivers
  }
)

export const agencyCaseManagers$ = createSelector(common, (common = {}) =>
  get(common, ['agencyCaseManagers'], [])
)

export const agencyCaseManagersDropdown$ = createSelector(
  agencyCaseManagers$,
  (agencyCaseManagers = []) =>
    agencyCaseManagers.map(({ uuid = '', firstName = '', lastName = '' }) => ({
      key: uuid,
      title: `${firstName} ${lastName}`,
      value: uuid
    }))
)

export const agencyCaseManagersDropdownWithAll$ = createSelector(
  agencyCaseManagersDropdown$,
  (agencyCaseManagers = []) =>
    addAllValue(
      [...agencyCaseManagers, NO_AGENCY_CASE_MANAGER_DROPDOWN_VALUE],
      true,
      'All'
    )
)

export const actionCounter$ = createSelector(
  common,
  (common = {}) => common?.actionCounter || []
)

export const taskCounter$ = createSelector(
  actionCounter$,
  (actionCounter = []) => {
    const taskCounter =
      actionCounter?.find(({ actionType }: any) => actionType === 'task')
        ?.actions || 0

    return taskCounter
  }
)

export const careGapCounter$ = createSelector(
  actionCounter$,
  (actionCounter = []) => {
    const careGapCounter =
      actionCounter?.find(({ actionType }: any) => actionType === 'careGap')
        ?.actions || 0

    return careGapCounter
  }
)

export const notificationCounter$ = createSelector(
  actionCounter$,
  (actionCounter = []) => {
    const notificationCounter =
      actionCounter?.find(
        ({ actionType }: any) => actionType === 'notification'
      )?.actions || 0

    return notificationCounter
  }
)

export const activityReasonTypes$ = createSelector(
  companyActivityTypesSelector,
  companyEducationRefSelector,
  companyCheckInRefSelector,
  backfillCarePlan$,
  companyMeasurementRefSelector,
  companyTelemedicineRefs$,
  backfillMedication$,
  (
    activityReasons = [],
    educationRef = [],
    checkInRef = [],
    backfillCarePlanRef = [],
    measurementRef = [],
    telemedicineRefs = [],
    backfillMedication = []
  ) =>
    flow(
      (items = []) =>
        items.map(({ uuid, refValue }) => ({
          key: uuid,
          value: uuid,
          title: refValue
        })),
      (items = []) => sortBy(items, 'title')
    )([
      ...activityReasons,
      ...educationRef,
      ...checkInRef,
      ...backfillCarePlanRef,
      ...measurementRef,
      ...telemedicineRefs,
      ...backfillMedication
    ])
)
